import React from 'react';
import { toJS } from 'mobx';

import { computeRevertUpdates, differs } from './revert-diff';
import {
  HIDDEN_COLUMNS,
  NodeColumns,
  TableRow,
  VISUAL_COLUMN_ORDER,
  tableStore,
} from '../../../stores/table-store';

/**
 * The columns Cancel can restore, plus the JSON order-map siblings that travel
 * with them. Anything outside this set is not reachable from the panel, so
 * snapshotting it would let Cancel undo an edit the user made in the GRID.
 */
const revertableColumns = (): string[] => {
  const columns = VISUAL_COLUMN_ORDER.filter(
    (column) => !HIDDEN_COLUMNS.has(column)
  ) as string[];
  return [...columns, ...columns.map((column) => `${column}Map`)];
};

const snapshotRow = (rowId: string): Record<string, unknown> => {
  const snapshot: Record<string, unknown> = {};
  revertableColumns().forEach((column) => {
    snapshot[column] = toJS(
      tableStore.getValue(rowId, column as keyof TableRow)
    );
  });
  return snapshot;
};

/**
 * Dirty tracking, Cancel and Save for the node property panel.
 *
 * ## Why Save is not what it looks like
 *
 * The panel COMMITS AS YOU TYPE. Every field writes through the same
 * draft-then-commit-on-blur path the grid and the Expanded Cell View use, which
 * is what keeps one undo entry per edit (PSTUDIO-2154) and keeps the raw draft
 * typeable (PSTUDIO-3882). Turning the panel into a buffered form would have
 * meant a second, panel-only write path and giving up both.
 *
 * So the footer means:
 *
 * - **"There are unsaved changes"** — this node differs from where it stood
 *   when the panel bound to it. Honest, and the same thing the design's
 *   indicator conveys.
 * - **Save Changes** — flush, WAIT for the write to land, and only then move
 *   the revert point. See below.
 * - **Cancel** — a real revert to that point, applied as ONE undo entry so
 *   Cmd+Z puts the changes back if it was hit by mistake.
 *
 * The revert point is taken when the panel BINDS to a node and moved on Save.
 * Note the consequence: closing the panel and reopening it on the same node
 * takes a fresh revert point, so Cancel reverts to the last time the panel was
 * opened, not to the beginning of the session. That is the same scope the
 * design's Cancel implies, but it is worth knowing it is not an undo stack.
 *
 * ## Save confirms the write; it does not assume it
 *
 * `tableStore.saveUnsavedChanges()` only QUEUES: it hands the dirty rows to the
 * save queue and clears the dirty markers straight away, without awaiting the
 * database. Calling it alone meant Save could not tell a committed write from a
 * failed one, and a save that errored would look identical to one that worked.
 *
 * So Save also awaits `waitForPendingSaves()` — the same primitive the publish
 * modal uses before it lets the backend read the nodes. It flushes both save
 * queues and awaits every in-flight write, and it THROWS if one fails. On a
 * throw the revert point stays put, so the node is still reported dirty and the
 * button stays enabled: the user can try again, and nothing has claimed to be
 * saved that is not.
 *
 * ## Why the snapshot is state and not a ref
 *
 * It was a ref, and that was the bug behind "the Save button stays enabled".
 * Mutating a ref does not re-render, and a successful Save changes no
 * observable this panel reads — it only clears a dirty SET the panel never
 * looks at. So the footer kept rendering `isDirty` from the pre-Save snapshot
 * forever. Cancel appeared to work only because it writes real values through
 * `updateRow`, and the observer re-renders on those.
 */
/** What the footer is currently reporting about the save. */
export type NodeSaveState = 'idle' | 'saving' | 'error';

type RevertPoint = { rowId: string; snapshot: Record<string, unknown> };

export const useNodeRevertPoint = (rowId: string | null) => {
  const [bound, setBound] = React.useState<RevertPoint | null>(null);
  const [saveState, setSaveState] = React.useState<NodeSaveState>('idle');

  // Taken during render rather than in an effect, so the very first frame after
  // binding already has a revert point. A render-phase state update is the
  // documented way to derive state from a changing input: React re-renders
  // immediately, before committing, and this cannot loop because the next pass
  // sees `bound.rowId === rowId`.
  if (rowId && bound?.rowId !== rowId) {
    setBound({ rowId, snapshot: snapshotRow(rowId) });
    if (saveState !== 'idle') setSaveState('idle');
  }

  // The row the panel is bound to RIGHT NOW, readable from an async callback
  // that closed over an older one. `save` awaits the write, and by the time it
  // resolves the user may have clicked another node.
  const boundRowIdRef = React.useRef<string | null>(rowId);
  boundRowIdRef.current = rowId;

  const snapshot = bound?.rowId === rowId ? bound.snapshot : {};

  // Read inside the component's render so the MobX observer subscribes to every
  // column it compares; a `computed` or an effect here would not re-render the
  // footer when a field changes.
  const isDirty = rowId
    ? Object.keys(snapshot).some((column) =>
        differs(
          snapshot[column],
          toJS(tableStore.getValue(rowId, column as keyof TableRow))
        )
      )
    : false;

  const save = React.useCallback(async () => {
    if (!rowId) return;

    setSaveState('saving');
    // Queues the dirty rows. Note this NO-OPS when `unsavedRowIds` is empty,
    // which happens routinely: a select or checkbox commits through
    // `writeCellValue` (`saveImmediately: true`), which reaches the save queue
    // without ever marking the row dirty. The wait below is what actually
    // covers those.
    tableStore.saveUnsavedChanges();

    try {
      await tableStore.waitForPendingSaves();
    } catch {
      // Leave the revert point where it is: the node stays dirty, Save stays
      // enabled, and nothing claims to be saved that is not.
      //
      // Only if the panel is still on THIS node. The bind-time reset above
      // already clears `saveState` when the row changes, so setting `error`
      // unconditionally would re-raise "Couldn't save your changes" on whatever
      // node the user moved to — a clean node, with both buttons disabled and
      // no way to dismiss the message.
      if (boundRowIdRef.current === rowId) setSaveState('error');
      return;
    }

    // The panel may have moved to another node while the write was in flight.
    // Rebaselining then would silently accept THIS row's edits against THAT
    // row's snapshot.
    setBound((current) =>
      current?.rowId === rowId
        ? { rowId, snapshot: snapshotRow(rowId) }
        : current
    );
    setSaveState('idle');
  }, [rowId]);

  const revert = React.useCallback(() => {
    if (!rowId || bound?.rowId !== rowId) return;

    const updates = computeRevertUpdates(bound.snapshot, (column) =>
      toJS(tableStore.getValue(rowId, column as keyof TableRow))
    );

    setSaveState('idle');

    if (Object.keys(updates).length === 0) return;

    // One undo entry for the whole revert. `recordRowEdit` is the same wrapper
    // the node-type change uses to bundle its cleared fields, so an accidental
    // Cancel is a single Cmd+Z away from being put back.
    tableStore.recordRowEdit(rowId, 'Revert node changes', () => {
      tableStore.updateRow({
        rowId,
        updates: updates as Partial<TableRow>,
        skipValidationRules: false,
        saveImmediately: true,
      });
      Object.keys(updates).forEach((column) => {
        tableStore.clearCellValidationError(rowId, column as NodeColumns);
      });
    });

    setBound({ rowId, snapshot: snapshotRow(rowId) });
  }, [rowId, bound]);

  return { isDirty, revert, save, saveState };
};
